/**
 * Yugen Build Journey
 * A calm chapter-card state machine: one step active at a time, with Next/Prev
 * controls, clickable progress dots, and mobile swipe. No scroll-jacking, no
 * external libraries. Scoped to the <yugen-build-journey> custom element.
 */
(function () {
  'use strict';

  var SWIPE_THRESHOLD = 45;
  var COMPARE_DEFAULT = 50;
  var COMPARE_KEY_STEP = 5;

  class YugenBuildJourney extends HTMLElement {
    constructor() {
      super();
      this.index = 0;
      this.onPrev = this.onPrev.bind(this);
      this.onNext = this.onNext.bind(this);
      this.onDotClick = this.onDotClick.bind(this);
      this.onTouchStart = this.onTouchStart.bind(this);
      this.onTouchMove = this.onTouchMove.bind(this);
      this.onTouchEnd = this.onTouchEnd.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onComparePointerDown = this.onComparePointerDown.bind(this);
      this.onComparePointerMove = this.onComparePointerMove.bind(this);
      this.onComparePointerUp = this.onComparePointerUp.bind(this);
      this.onCompareKeyDown = this.onCompareKeyDown.bind(this);
    }

    connectedCallback() {
      this.steps = Array.from(this.querySelectorAll('[data-yj-step]'));
      this.dots = Array.from(this.querySelectorAll('[data-yj-dot]'));
      this.prevBtn = this.querySelector('[data-yj-prev]');
      this.nextBtn = this.querySelector('[data-yj-next]');
      this.stage = this.querySelector('[data-yj-stage]');
      this.compareSliders = Array.from(this.querySelectorAll('[data-yj-compare]'));
      this.activeCompare = null;

      if (this.steps.length === 0) return;

      if (this.prevBtn) this.prevBtn.addEventListener('click', this.onPrev);
      if (this.nextBtn) this.nextBtn.addEventListener('click', this.onNext);
      this.dots.forEach((dot) => dot.addEventListener('click', this.onDotClick));

      if (this.stage) {
        this.stage.addEventListener('touchstart', this.onTouchStart, { passive: true });
        this.stage.addEventListener('touchmove', this.onTouchMove, { passive: true });
        this.stage.addEventListener('touchend', this.onTouchEnd, { passive: true });
      }

      this.addEventListener('shopify:block:select', this.onBlockSelect);

      this.compareSliders.forEach(function (compare) {
        compare.addEventListener('pointerdown', this.onComparePointerDown);
        compare.addEventListener('pointermove', this.onComparePointerMove);
        compare.addEventListener('pointerup', this.onComparePointerUp);
        compare.addEventListener('pointercancel', this.onComparePointerUp);

        var handle = compare.querySelector('[data-yj-compare-handle]');
        if (handle) handle.addEventListener('keydown', this.onCompareKeyDown);
      }, this);

      this.setActive(0, false);
    }

    disconnectedCallback() {
      if (this.prevBtn) this.prevBtn.removeEventListener('click', this.onPrev);
      if (this.nextBtn) this.nextBtn.removeEventListener('click', this.onNext);
      if (this.dots) this.dots.forEach((dot) => dot.removeEventListener('click', this.onDotClick));
      if (this.stage) {
        this.stage.removeEventListener('touchstart', this.onTouchStart);
        this.stage.removeEventListener('touchmove', this.onTouchMove);
        this.stage.removeEventListener('touchend', this.onTouchEnd);
      }
      this.removeEventListener('shopify:block:select', this.onBlockSelect);

      if (this.compareSliders) {
        this.compareSliders.forEach(function (compare) {
          compare.removeEventListener('pointerdown', this.onComparePointerDown);
          compare.removeEventListener('pointermove', this.onComparePointerMove);
          compare.removeEventListener('pointerup', this.onComparePointerUp);
          compare.removeEventListener('pointercancel', this.onComparePointerUp);

          var handle = compare.querySelector('[data-yj-compare-handle]');
          if (handle) handle.removeEventListener('keydown', this.onCompareKeyDown);
        }, this);
      }
    }

    onPrev() {
      this.setActive(this.index - 1);
    }

    onNext() {
      this.setActive(this.index + 1);
    }

    onDotClick(event) {
      var i = parseInt(event.currentTarget.getAttribute('data-yj-dot'), 10);
      if (!isNaN(i)) this.setActive(i);
    }

    onComparePointerDown(event) {
      if (event.button !== 0 && event.pointerType === 'mouse') return;

      var compare = event.currentTarget;
      this.activeCompare = compare;
      compare.setPointerCapture(event.pointerId);
      this.setComparePosition(compare, event.clientX);
      event.preventDefault();
    }

    onComparePointerMove(event) {
      if (!this.activeCompare || event.currentTarget !== this.activeCompare) return;
      this.setComparePosition(this.activeCompare, event.clientX);
      event.preventDefault();
    }

    onComparePointerUp(event) {
      if (!this.activeCompare || event.currentTarget !== this.activeCompare) return;

      try {
        this.activeCompare.releasePointerCapture(event.pointerId);
      } catch (err) {
        /* pointer already released */
      }

      this.activeCompare = null;
    }

    onCompareKeyDown(event) {
      var compare = event.currentTarget.closest('[data-yj-compare]');
      if (!compare) return;

      var current = parseFloat(getComputedStyle(compare).getPropertyValue('--yj-compare')) || COMPARE_DEFAULT;
      var next = current;

      switch (event.key) {
        case 'ArrowLeft':
        case 'ArrowDown':
          next = current - COMPARE_KEY_STEP;
          break;
        case 'ArrowRight':
        case 'ArrowUp':
          next = current + COMPARE_KEY_STEP;
          break;
        case 'Home':
          next = 0;
          break;
        case 'End':
          next = 100;
          break;
        default:
          return;
      }

      event.preventDefault();
      this.setComparePosition(compare, null, next);
    }

    setComparePosition(compare, clientX, percentOverride) {
      var pct;

      if (typeof percentOverride === 'number') {
        pct = percentOverride;
      } else {
        var rect = compare.getBoundingClientRect();
        if (!rect.width) return;
        pct = ((clientX - rect.left) / rect.width) * 100;
      }

      pct = Math.max(0, Math.min(100, pct));
      compare.style.setProperty('--yj-compare', String(pct));

      var handle = compare.querySelector('[data-yj-compare-handle]');
      if (handle) handle.setAttribute('aria-valuenow', String(Math.round(pct)));
    }

    resetCompareSliders() {
      if (!this.compareSliders) return;

      this.compareSliders.forEach(function (compare) {
        compare.style.setProperty('--yj-compare', String(COMPARE_DEFAULT));

        var handle = compare.querySelector('[data-yj-compare-handle]');
        if (handle) handle.setAttribute('aria-valuenow', String(COMPARE_DEFAULT));
      });

      this.activeCompare = null;
    }

    onBlockSelect(event) {
      var step = event.target.closest('[data-yj-step]');
      if (!step) return;
      var i = this.steps.indexOf(step);
      if (i !== -1) this.setActive(i);
    }

    onTouchStart(event) {
      if (event.target.closest('[data-yj-compare]')) return;

      var t = event.changedTouches[0];
      this.touchStartX = t.clientX;
      this.touchStartY = t.clientY;
      this.touchDX = 0;
      this.touchDY = 0;
    }

    onTouchMove(event) {
      if (this.touchStartX == null) return;
      var t = event.changedTouches[0];
      this.touchDX = t.clientX - this.touchStartX;
      this.touchDY = t.clientY - this.touchStartY;
    }

    onTouchEnd() {
      if (this.touchStartX == null) return;

      var dx = this.touchDX || 0;
      var dy = this.touchDY || 0;
      if (Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy) * 1.4) {
        if (dx < 0) {
          this.setActive(this.index + 1);
        } else {
          this.setActive(this.index - 1);
        }
      }
      this.touchStartX = null;
      this.touchStartY = null;
    }

    setActive(next, focusArrow) {
      var max = this.steps.length - 1;
      var i = Math.max(0, Math.min(next, max));
      this.index = i;

      this.resetCompareSliders();

      this.steps.forEach(function (step, idx) {
        var active = idx === i;
        step.classList.toggle('is-active', active);
        step.setAttribute('aria-hidden', active ? 'false' : 'true');
        step.style.setProperty('--yj-dir', idx < i ? -1 : idx > i ? 1 : 0);
      });

      this.dots.forEach(function (dot, idx) {
        var active = idx === i;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });

      if (this.prevBtn) this.prevBtn.disabled = i === 0;
      if (this.nextBtn) this.nextBtn.disabled = i === max;

      if (focusArrow !== false) {
        if (i === 0 && this.prevBtn && document.activeElement === this.prevBtn && this.nextBtn) {
          this.nextBtn.focus();
        } else if (i === max && this.nextBtn && document.activeElement === this.nextBtn && this.prevBtn) {
          this.prevBtn.focus();
        }
      }
    }
  }

  if (!customElements.get('yugen-build-journey')) {
    customElements.define('yugen-build-journey', YugenBuildJourney);
  }
})();
